import { isValid, parse, parseISO } from "date-fns";
import Head from "expo-router/head";
import React, { useMemo, useRef, useState } from "react";
import { Platform, StyleSheet, Text, View } from "react-native";

import { useTranslation } from "@luna-restaurant/i18n";
import type {
  SchoolCafeteria,
  SchoolClass,
  SchoolLunchBox,
  SchoolLunchBoxOrder,
  SchoolStudentGroup,
} from "@luna-restaurant/school-api";
import {
  PageContainer,
  ThemedFeedback,
  ThemedHeadlineText,
  ThemedLoader,
  ThemedTable,
  type ThemedTableColumn,
  type ThemedTableHeaderColumn,
  ThemedTitle,
  useCustomTheme,
} from "@luna-restaurant/ui";

import { ContactBanner } from "@/components/ContactBanner";
import { SecondaryButton } from "@/components/SecondaryButton";
import { StatusBannerHost } from "@/components/StatusBannerHost";
import {
  NewLunchBoxesForm,
  type NewLunchBoxesFormValues,
} from "@/components/forms/NewLunchBoxesForm";
import { getLunchBoxDeliveryTimeTranslationKey } from "@/components/forms/lunchBoxDeliveryTime";
import {
  DATE_ONLY_FORMAT,
  MIN_BUSINESS_DAYS_FOR_LUNCH_BOX_CANCELLATION,
  hasEnoughBusinessDaysBeforeLunchBoxCancellation,
} from "@/components/forms/lunchBoxOrderLeadTime";
import {
  CancelLunchBoxModal,
  type CancelLunchBoxModalOrder,
} from "@/components/modals/CancelLunchBoxModal";
import { useCollection } from "@/hooks/useCollection";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useRequest } from "@/hooks/useRequest";
import { useScrollIntoView } from "@/hooks/useScrollIntoView";
import { useStatusBanner } from "@/hooks/useStatusBanner";
import { getStatusBannerErrorMessage } from "@/utils/getStatusBannerErrorMessage";

type LunchBoxStatus = "active" | "cancelled";

type LunchBoxOrder = {
  id: string;
  deliveryDateRaw: string;
  deliveryDate: string;
  deliveryTime: string;
  quantity: string;
  lunchPaketName: string;
  classLabel: string;
  address: string;
  nameOfCreator: string;
  comment: string;
  status: LunchBoxStatus;
  isCancellable: boolean;
};

const boldText = { fontWeight: "bold", fontFamily: "PoppinsBold" } as const;
const mediumText = { fontFamily: "PoppinsMedium", fontWeight: "500" } as const;
const valuePlaceholder = "--";
const formatCafeteriaAddress = (cafeteria: SchoolCafeteria) =>
  `${cafeteria.streetAddress}, ${cafeteria.postalCode} ${cafeteria.city}`;
const toCanonicalCafeteriaIri = (cafeteria: SchoolCafeteria) => {
  if (/\/schools\/\d+\/cafeterias\/\d+\/?$/.test(cafeteria["@id"])) {
    return `/v1/cafeterias/${cafeteria.id}`;
  }

  return cafeteria["@id"];
};

const formatDate = (value: string, language: string) => {
  const parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? parse(value, DATE_ONLY_FORMAT, new Date())
    : parseISO(value);

  if (!isValid(parsedDate)) {
    return value;
  }

  return parsedDate.toLocaleDateString(language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export default function LunchBoxesScreen() {
  const formSectionRef = useRef<View | null>(null);
  const overviewHeadlineRef = useRef<View | null>(null);
  const { fonts, colors } = useCustomTheme();
  const { t, i18n } = useTranslation();
  const { currentUser, isLoadingUser } = useCurrentUser();
  const request = useRequest();
  const handleScrollToForm = useScrollIntoView(formSectionRef);
  const handleScrollToOverviewHeadline = useScrollIntoView(overviewHeadlineRef);
  const { showStatusBanner } = useStatusBanner();
  const [locallyCancelledOrderIds, setLocallyCancelledOrderIds] = useState<
    string[]
  >([]);
  const [selectedOrder, setSelectedOrder] = useState<LunchBoxOrder | null>(
    null,
  );
  const [cancelValidationError, setCancelValidationError] = useState<
    string | null
  >(null);
  const [isCancellingLunchBoxOrder, setIsCancellingLunchBoxOrder] =
    useState(false);
  const schoolClassesQuery = useCollection<SchoolClass>({
    queryKey: ["school-classes", currentUser?.school],
    path: currentUser?.school ? `${currentUser.school}/classes` : "",
    enabled: !!currentUser?.school,
    fetchAllPages: true,
    staleTime: 5 * 60 * 1000,
  });
  const schoolCafeteriasQuery = useCollection<SchoolCafeteria>({
    queryKey: ["school-cafeterias", currentUser?.school],
    path: currentUser?.school ? `${currentUser.school}/cafeterias` : "",
    enabled: !!currentUser?.school,
    fetchAllPages: true,
    staleTime: 5 * 60 * 1000,
  });
  const schoolStudentGroupsQuery = useCollection<SchoolStudentGroup>({
    queryKey: ["school-student-groups", currentUser?.school],
    path: currentUser?.school ? `${currentUser.school}/student-groups` : "",
    enabled: !!currentUser?.school,
    fetchAllPages: true,
    staleTime: 5 * 60 * 1000,
  });
  const lunchBoxesCatalogQuery = useCollection<SchoolLunchBox>({
    queryKey: ["lunch-boxes"],
    path: "/v1/lunch-boxes",
    enabled: !isLoadingUser,
    fetchAllPages: true,
    staleTime: 5 * 60 * 1000,
  });
  const lunchBoxOrdersQuery = useCollection<SchoolLunchBoxOrder>({
    queryKey: ["lunch-box-orders", currentUser?.school],
    path: currentUser?.school ? `${currentUser.school}/lunch-box-orders` : "",
    enabled: !!currentUser?.school,
    fetchAllPages: true,
    query: { "order[deliveryDate]": "asc" },
  });
  const isLoading = isLoadingUser || lunchBoxOrdersQuery.isLoading;
  const isError = lunchBoxOrdersQuery.isError;

  const schoolClassById = useMemo(() => {
    const map = new Map<string, SchoolClass>();
    for (const schoolClass of schoolClassesQuery.items) {
      map.set(schoolClass["@id"], schoolClass);
    }
    return map;
  }, [schoolClassesQuery.items]);
  const schoolStudentGroupById = useMemo(() => {
    const map = new Map<string, SchoolStudentGroup>();
    for (const schoolStudentGroup of schoolStudentGroupsQuery.items) {
      map.set(schoolStudentGroup["@id"], schoolStudentGroup);
    }
    return map;
  }, [schoolStudentGroupsQuery.items]);
  const schoolClassOptions = useMemo(
    () =>
      schoolClassesQuery.items.map((schoolClass) => ({
        label: schoolClass.name,
        value: schoolClass["@id"],
      })),
    [schoolClassesQuery.items],
  );
  const cafeteriaOptions = useMemo(
    () =>
      schoolCafeteriasQuery.items.map((cafeteria) => ({
        label: formatCafeteriaAddress(cafeteria),
        value: toCanonicalCafeteriaIri(cafeteria),
      })),
    [schoolCafeteriasQuery.items],
  );
  const cafeteriaAddressByIri = useMemo(() => {
    const map = new Map<string, string>();
    for (const cafeteria of schoolCafeteriasQuery.items) {
      const address = formatCafeteriaAddress(cafeteria);
      map.set(cafeteria["@id"], address);
      map.set(toCanonicalCafeteriaIri(cafeteria), address);
    }
    return map;
  }, [schoolCafeteriasQuery.items]);
  const lunchBoxById = useMemo(() => {
    const map = new Map<string, SchoolLunchBox>();
    for (const lunchBox of lunchBoxesCatalogQuery.items) {
      map.set(lunchBox["@id"], lunchBox);
    }
    return map;
  }, [lunchBoxesCatalogQuery.items]);
  const locallyCancelledOrderIdSet = useMemo(
    () => new Set(locallyCancelledOrderIds),
    [locallyCancelledOrderIds],
  );

  const lunchBoxes = useMemo<LunchBoxOrder[]>(() => {
    const nowForCancellationCheck = new Date();

    return lunchBoxOrdersQuery.items.map((order) => {
      const orderId = String(order.id);
      const schoolClassName = order.schoolClass
        ? (schoolClassById.get(order.schoolClass)?.name ?? valuePlaceholder)
        : undefined;
      const schoolStudentGroupName = order.studentGroup
        ? (schoolStudentGroupById.get(order.studentGroup)?.name ??
          valuePlaceholder)
        : undefined;
      const isCancelled =
        Boolean(order.cancelledAt) || locallyCancelledOrderIdSet.has(orderId);
      const isCancellable =
        !isCancelled &&
        hasEnoughBusinessDaysBeforeLunchBoxCancellation(
          order.deliveryDate,
          nowForCancellationCheck,
        );

      return {
        id: orderId,
        deliveryDateRaw: order.deliveryDate,
        deliveryDate: formatDate(order.deliveryDate, i18n.language),
        deliveryTime: t(
          getLunchBoxDeliveryTimeTranslationKey(order.deliveryTime),
        ),
        quantity: String(order.quantity),
        lunchPaketName: order.lunchBox
          ? (lunchBoxById.get(order.lunchBox)?.title ?? valuePlaceholder)
          : valuePlaceholder,
        classLabel:
          schoolStudentGroupName ??
          schoolClassName ??
          t("closeTimes.form.scopeOptions.school"),
        address:
          (order.cafeteria
            ? cafeteriaAddressByIri.get(order.cafeteria)
            : undefined) ?? valuePlaceholder,
        nameOfCreator: order.editedBy ?? valuePlaceholder,
        comment: order.comment || valuePlaceholder,
        status: isCancelled ? "cancelled" : "active",
        isCancellable,
      };
    });
  }, [
    locallyCancelledOrderIdSet,
    lunchBoxOrdersQuery.items,
    schoolClassById,
    schoolStudentGroupById,
    i18n.language,
    t,
    lunchBoxById,
    cafeteriaAddressByIri,
  ]);

  const cancelledLabelStyle = {
    ...fonts.bold,
    color: colors.muted, // Accessibility check for colors.muted passes partially. Design's color (#B2B2B2, gray70) fails every test on https://webaim.org/resources/contrastchecker/
    borderRadius: 4,
    borderColor: colors.muted,
    borderWidth: 1,
    paddingHorizontal: 8,
    fontSize: 16,
    lineHeight: 20.8,
    alignSelf:
      Platform.OS === "web" ? ("center" as const) : ("flex-start" as const),
    marginRight: Platform.OS === "web" ? 0 : 16,
  };

  const header = (
    <>
      <ThemedTitle
        subtitle={t("lunchBoxes.subtitle")}
        title={t("lunchBoxes.title")}
        icon={{
          iconSet: "font-awesome",
          icon: "crate-apple",
          style: "regular",
        }}
      />
      <StatusBannerHost />
    </>
  );

  const headerColumns: ThemedTableHeaderColumn[] = [
    {
      width: 21,
      parts: [
        {
          label: t("lunchBoxes.table.header.deliveryDate"),
          style: boldText,
        },
        {
          label: t("lunchBoxes.table.header.deliveryTime"),
          style: mediumText,
        },
      ],
    },
    {
      parts: [
        {
          label: t("lunchBoxes.table.header.quantity"),
          style: boldText,
        },
        {
          label: t("lunchBoxes.table.header.lunchPaketName"),
          style: mediumText,
        },
      ],
      width: 21,
    },
    {
      parts: [
        {
          label: t("lunchBoxes.table.header.schoolClass"),
          style: boldText,
        },
        {
          label: t("lunchBoxes.table.header.address"),
          style: mediumText,
        },
      ],
      width: 37,
    },
    {
      label: t("lunchBoxes.table.header.nameOfCreator"),
      width: 37,
    },
    {
      label: t("lunchBoxes.table.header.comment"),
      width: 59,
    },
    {
      label: t("lunchBoxes.table.header.actions"),
      width: 23,
    },
  ];

  const createTableColumns = (order: LunchBoxOrder): ThemedTableColumn[] => {
    const baseColumns: ThemedTableColumn[] = [
      {
        type: "text",
        parts: [
          { label: order.deliveryDate, style: boldText },
          { label: order.deliveryTime },
        ],
      },
      {
        type: "text",
        parts: [
          { label: order.quantity, style: boldText },
          { label: order.lunchPaketName },
        ],
      },
      {
        type: "text",
        parts: [
          { label: order.classLabel, style: boldText },
          { label: order.address },
        ],
      },
      { type: "text", label: order.nameOfCreator },
      { type: "text", label: order.comment },
    ];

    const actionColumn: ThemedTableColumn =
      order.status === "cancelled"
        ? {
            type: "text",
            label: t("lunchBoxes.table.row.actions.cancelled"),
            style: cancelledLabelStyle,
          }
        : !order.isCancellable
          ? {
              type: "text",
              label: "",
            }
          : {
              type: "action",
              actions: [
                {
                  label: t("lunchBoxes.table.row.actions.delete"),
                  icon: "close-circle",
                  onPress: () => {
                    setCancelValidationError(null);
                    setSelectedOrder(order);
                  },
                },
              ],
            };

    return [...baseColumns, actionColumn];
  };

  const handleCancelConfirm = async (modalOrder: CancelLunchBoxModalOrder) => {
    if (!currentUser?.school) {
      console.error("Cannot cancel lunch box order without a school");
      return;
    }

    if (
      !hasEnoughBusinessDaysBeforeLunchBoxCancellation(
        modalOrder.deliveryDateRaw,
        new Date(),
      )
    ) {
      setCancelValidationError(
        t("lunchBoxes.cancelValidation.leadTimeNotMet", {
          minBusinessDays: MIN_BUSINESS_DAYS_FOR_LUNCH_BOX_CANCELLATION,
        }),
      );
      return;
    }

    setCancelValidationError(null);
    setIsCancellingLunchBoxOrder(true);
    try {
      await request(
        `${currentUser.school}/lunch-box-orders/${modalOrder.id}/cancel`,
        {
          method: "POST",
        },
      );

      setLocallyCancelledOrderIds((previousIds) =>
        previousIds.includes(modalOrder.id)
          ? previousIds
          : [...previousIds, modalOrder.id],
      );
      setSelectedOrder(null);
      await lunchBoxOrdersQuery.refetch();
    } catch (error) {
      console.error("Failed to cancel lunch box order:", error);
      showStatusBanner({
        type: "error",
        message: getStatusBannerErrorMessage(error, t),
        dismissible: true,
        dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
        autoDismissAfterSeconds: 20,
      });
      setSelectedOrder(null);
    } finally {
      setIsCancellingLunchBoxOrder(false);
    }
  };

  const modalOrder: CancelLunchBoxModalOrder | null = selectedOrder
    ? {
        id: selectedOrder.id,
        deliveryDateRaw: selectedOrder.deliveryDateRaw,
        deliveryDate: selectedOrder.deliveryDate,
        deliveryTime: selectedOrder.deliveryTime,
        quantity: selectedOrder.quantity,
        lunchPaketName: selectedOrder.lunchPaketName,
        classLabel: selectedOrder.classLabel,
        address: selectedOrder.address,
      }
    : null;

  const handleLunchBoxSubmitSuccess = (values: NewLunchBoxesFormValues) => {
    showStatusBanner({
      type: "success",
      message: t("lunchBoxes.form.successBanner.message", {
        date: formatDate(values.date, i18n.language),
      }),
      onViewPress: handleScrollToOverviewHeadline,
      viewButtonLabel: t("common.form.successBanner.viewLabel"),
      dismissible: true,
      dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
      autoDismissAfterSeconds: 20,
    });
    void lunchBoxOrdersQuery.refetch();
    handleScrollToForm();
  };

  const handleLunchBoxSubmitError = (error: unknown) => {
    showStatusBanner({
      type: "error",
      message: getStatusBannerErrorMessage(error, t),
      dismissible: true,
      dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
      autoDismissAfterSeconds: 20,
    });
    handleScrollToForm();
  };

  return (
    <View>
      <Head>
        <title>{t("titles.lunchBoxes")}</title>
      </Head>
      <PageContainer header={header} style={styles.pageContainer}>
        <View ref={formSectionRef} style={styles.sectionLunchBoxForm}>
          <ThemedHeadlineText
            type={t("lunchBoxes.type.new")}
            headline={t("lunchBoxes.createLunchBox")}
            textContent={t("lunchBoxes.createLunchBoxText")}
          />
          <NewLunchBoxesForm
            onSubmitSuccess={handleLunchBoxSubmitSuccess}
            onSubmitInvalid={handleScrollToForm}
            onSubmitError={handleLunchBoxSubmitError}
            schoolClassOptions={schoolClassOptions}
            cafeteriaOptions={cafeteriaOptions}
            lunchBoxes={lunchBoxesCatalogQuery.items}
            schoolPath={currentUser?.school}
          />
        </View>

        <View style={styles.sectionLunchBoxOverview}>
          <View ref={overviewHeadlineRef} style={styles.overviewHeadline}>
            <ThemedHeadlineText
              type={t("lunchBoxes.type.current")}
              headline={t("lunchBoxes.lunchBoxOverview")}
              textContent={t("lunchBoxes.lunchBoxOverviewText")}
            />
          </View>

          {isLoading ? (
            <View style={styles.listStateContainer}>
              <ThemedLoader spinnerSize={50} />
            </View>
          ) : isError ? (
            <ThemedFeedback
              type="error"
              iconName="error-outline"
              subtitle={t("lunchBoxes.error.subtitle")}
              title={t("lunchBoxes.error.title")}
              text={t("lunchBoxes.error.text")}
              description={t("lunchBoxes.error.description")}
              onRetry={() => {
                void lunchBoxOrdersQuery.refetch();
              }}
              retryButtonLabel={t("lunchBoxes.error.retryButtonLabel")}
            />
          ) : lunchBoxes.length === 0 ? (
            <View style={styles.noLunchBoxesContainer}>
              <Text style={[styles.noLunchBoxesText, fonts.regular]}>
                {t("lunchBoxes.noLunchBoxes.text")}
              </Text>
              <SecondaryButton
                label={t("lunchBoxes.newLunchBoxButtonText")}
                iconName="plus"
                iconPosition="left"
                onPress={handleScrollToForm}
              />
            </View>
          ) : (
            <View style={styles.tableContainer}>
              <ThemedTable headerColumns={headerColumns}>
                {lunchBoxes.map((order) => (
                  <ThemedTable.Row
                    key={order.id}
                    columns={createTableColumns(order)}
                  />
                ))}
              </ThemedTable>
            </View>
          )}
        </View>
      </PageContainer>
      <ContactBanner />
      <CancelLunchBoxModal
        visible={Boolean(selectedOrder)}
        order={modalOrder}
        isSubmitting={isCancellingLunchBoxOrder}
        onDismiss={() => {
          if (!isCancellingLunchBoxOrder) {
            setCancelValidationError(null);
            setSelectedOrder(null);
          }
        }}
        validationError={cancelValidationError}
        onConfirm={(order) => {
          void handleCancelConfirm(order);
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    gap: 120,
    paddingTop: 32,
  },
  sectionLunchBoxForm: {
    paddingTop: 32,
    gap: 64,
  },
  sectionLunchBoxOverview: {
    gap: 64,
  },
  overviewHeadline: {
    marginTop: 0,
  },
  noLunchBoxesContainer: {
    paddingVertical: 32,
    alignItems: "center",
    gap: 32,
  },
  noLunchBoxesText: {
    fontSize: 32,
    lineHeight: 40,
  },
  tableContainer: {
    marginBottom: 32,
  },
  listStateContainer: {
    minHeight: 300,
  },
});
